import { Base, getInstanceFromElement } from '@studiometa/js-toolkit';
import { ModalWithTransition } from '@studiometa/ui';
import { lerp } from '@studiometa/js-toolkit/utils';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const MOBILE_BREAKPOINTS = new Set(['xxs', 'xs', 's']);

/**
 *
 */
export default class ReassuranceScroll extends Base {
  static config = {
    name: 'ReassuranceScroll',
    refs: [
      'navBar',
      'navBtns[]',
      'squares[]',
      'navLabels[]',
      'blocks[]',
      'imageWrappers[]',
      'blockImages[]',
      'navMasks[]',
      'teleport[]',
    ],
    components: {
      ModalWithTransition,
    },
  };

  /** @type {ScrollTrigger[]} */
  scrollTriggers = [];

  /**
   * @returns {boolean}
   */
  get isMobile() {
    return MOBILE_BREAKPOINTS.has(this.$services.get('resized').breakpoint);
  }

  /**
   *
   */
  mounted() {
    this.$refs.teleport.forEach((modal) => {
      document.body.append(modal);
    });

    if (this.isMobile) {
      return;
    }

    Object.assign(this.$refs.navBar.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
    });
    gsap.set(this.$refs.navBar, { autoAlpha: 0, y: -20 });

    const showNavBar = () =>
      gsap.to(this.$refs.navBar, {
        autoAlpha: 1,
        y: 0,
        duration: 0.4,
        ease: 'power2.out',
      });
    const hideNavBar = () =>
      gsap.to(this.$refs.navBar, {
        autoAlpha: 0,
        y: -20,
        duration: 0.3,
        ease: 'power2.in',
      });

    this.scrollTriggers.push(
      ScrollTrigger.create({
        trigger: this.$el,
        start: 'top top',
        end: 'bottom top',
        onEnter: showNavBar,
        onEnterBack: showNavBar,
        onLeave: hideNavBar,
        onLeaveBack: hideNavBar,
      }),
    );

    this.squareTargetSizes = this.$refs.squares.map((square) => {
      const size = square.offsetWidth;
      gsap.set(square, {
        width: 20,
        height: 20,
        borderBottomLeftRadius: 8,
        borderTopRightRadius: 8,
      });
      return size;
    });

    this.$refs.blocks.forEach((block, index) => {
      this.initBlockTrigger(block, index);
    });
  }

  /**
   *
   */
  destroyed() {
    this.scrollTriggers.forEach((st) => st.kill());
    this.scrollTriggers = [];

    this.$refs.teleport.forEach((modal) => {
      this.$el.append(modal);
    });
  }

  /**
   * @param {object} params
   * @param {number} params.index
   */
  onBlockImagesClick({ index }) {
    const btn = this.$refs.blockImages[index];
    if (!btn.dataset.modalTarget) {
      return;
    }
    const modalEl = document.querySelector(`#${btn.dataset.modalTarget}`);
    const instance = getInstanceFromElement(modalEl, ModalWithTransition);
    if (!instance) {
      return;
    }

    instance.open().then(() => {
      const video = modalEl.querySelector('video');
      if (video) {
        video.play();
      }
    });
    instance.$on('close', () => {
      const video = modalEl.querySelector('video');
      if (video) {
        video.pause();
      }
    });
  }

  /**
   * @param {object} params
   * @param {number} params.index
   */
  onNavBtnsClick({ index }) {
    if (this.isMobile) {
      return;
    }

    const block = this.$refs.blocks[index];
    if (!block) return;
    const top = block.getBoundingClientRect().top + window.scrollY - this.$refs.navBar.offsetHeight;
    gsap.killTweensOf(window);
    gsap.to(window, { scrollTo: top, duration: 0.8, ease: 'power3.out', overwrite: true });
  }

  /**
   * @param {HTMLElement} image
   */
  resetImageStyles(image) {
    image.style.cssText = '';
  }

  /**
   * @param {HTMLElement} image
   * @param {HTMLElement} wrapper
   * @param {HTMLElement} square
   */
  restoreImage(image, wrapper, square) {
    this.resetImageStyles(image);
    image.classList.add('aspect-[4/3]');
    wrapper.append(image);
    gsap.to(square, { borderColor: '', duration: 0.8, ease: 'power4.out' });
  }

  /**
   * @param {HTMLElement} image
   * @param {HTMLElement} square
   */
  parkImage(image, square) {
    image.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;border-radius:0 .87rem;object-fit:cover;';
    image.classList.remove('aspect-[4/3]');
    square.append(image);
    gsap.to(square, {
      borderColor: '#C3E914',
      clearProps: 'width,height,borderBottomLeftRadius,borderTopRightRadius',
      duration: 0.8,
      ease: 'power4.out',
    });
  }

  /**
   * @param {HTMLElement} block
   * @param {number} index
   */
  initBlockTrigger(block, index) {
    const image = this.$refs.blockImages[index];
    const square = this.$refs.squares[index];
    const wrapper = this.$refs.imageWrappers[index];
    const label = this.$refs.navLabels[index];

    if (!image || !square || !wrapper) {
      return;
    }

    const playBtn = image.tagName === 'BUTTON' ? image.querySelector('[data-play]') : null;
    const { width: startWidth, height: startHeight } = image.getBoundingClientRect();

    wrapper.style.height = `${startHeight}px`;

    let isInSquare = false;
    const nextBlock = this.$refs.blocks[index + 1];

    this.scrollTriggers.push(
      ScrollTrigger.create({
        trigger: wrapper,
        start: 'center center',
        end: () => {
          if (nextBlock) {
            const nextTop = nextBlock.getBoundingClientRect().top + window.scrollY;
            const wrapperCenter =
              wrapper.getBoundingClientRect().top + window.scrollY + wrapper.offsetHeight / 2;
            return `+=${Math.max((nextTop - wrapperCenter) * 0.8, 200)}`;
          }
          const prevBlock = this.$refs.blocks[index - 1];
          if (prevBlock) {
            const prevTop = prevBlock.getBoundingClientRect().top + window.scrollY;
            const currentTop = block.getBoundingClientRect().top + window.scrollY;
            return `+=${Math.max((currentTop - prevTop) * 0.55, 200)}`;
          }
          return `+=${window.innerHeight * 0.8}`;
        },

        onUpdate: (self) => {
          const p = self.progress;

          // Grow the square from 20px to target size, finishing at 70% progress
          const targetSize = this.squareTargetSizes[index];
          const squareSize = Math.min(lerp(20, targetSize, p / 0.7), targetSize);
          const squareRadius = Math.min(lerp(8, 16, p / 0.7), 16);
          gsap.set(square, {
            width: squareSize,
            height: squareSize,
            borderBottomLeftRadius: squareRadius,
            borderTopRightRadius: squareRadius,
          });

          if (p <= 0) {
            if (isInSquare) {
              this.restoreImage(image, wrapper, square);
              isInSquare = false;
            } else if (image.style.position === 'fixed') {
              this.resetImageStyles(image);
            }
            gsap.set(square, {
              width: 20,
              height: 20,
              borderBottomLeftRadius: 8,
              borderTopRightRadius: 8,
            });
            return;
          }

          if (p >= 1) {
            if (!isInSquare) {
              this.parkImage(image, square);
              isInSquare = true;
            }
            return;
          }

          if (isInSquare) {
            this.restoreImage(image, wrapper, square);
            isInSquare = false;
          }

          const wrapperRect = wrapper.getBoundingClientRect();
          const squareRect = square.getBoundingClientRect();

          image.style.position = 'fixed';
          image.style.zIndex = '20';
          image.style.pointerEvents = 'none';
          image.style.objectFit = 'cover';
          image.style.width = `${lerp(startWidth, squareRect.width, p)}px`;
          image.style.height = `${lerp(startHeight, squareRect.height, p)}px`;
          image.style.left = `${lerp(wrapperRect.left, squareRect.left, p)}px`;
          image.style.top = `${lerp(wrapperRect.top, squareRect.top, p)}px`;
          image.style.borderRadius = `0 ${lerp(16, 13.92, p)}px`;

          if (playBtn) {
            playBtn.style.transform = `scale(${lerp(1, 0, p)})`;
          }
        },

        onLeaveBack: () => {
          if (isInSquare) {
            this.restoreImage(image, wrapper, square);
            isInSquare = false;
          } else {
            this.resetImageStyles(image);
          }
        },
      }),
    );

    // Separate ScrollTrigger for the label — spans a much longer scroll distance
    if (label) {
      const mask = this.$refs.navMasks[index];
      gsap.set(label, { maxWidth: 0 });
      if (mask) {
        gsap.set(mask, { opacity: 1 });
      }

      this.scrollTriggers.push(
        ScrollTrigger.create({
          trigger: wrapper,
          start: 'center center',
          end: () => {
            if (nextBlock) {
              const nextTop = nextBlock.getBoundingClientRect().top + window.scrollY;
              const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
              return `+=${(nextTop - wrapperTop) * 1.3}`;
            }
            return `+=${window.innerHeight * 1.3}`;
          },
          onUpdate: (self) => {
            const p = self.progress;
            const widthProgress = p * p * (3 - 2 * p);
            gsap.set(label, {
              maxWidth: `${lerp(0, 300, widthProgress)}px`,
            });
            if (mask) {
              const maskProgress = Math.max(0, Math.min((widthProgress - 0.3) / 0.15, 1));
              gsap.set(mask, { opacity: 1 - maskProgress });
            }
          },
          onLeaveBack: () => {
            gsap.set(label, { maxWidth: 0 });
            if (mask) {
              gsap.set(mask, { opacity: 1 });
            }
          },
        }),
      );
    }
  }
}
